/**
 * The board-side DiagnosticObject the fixture fleet answers a probe with (T5, §۵.۸).
 *
 * `docs/ACS_MULTI_VENDOR_FA.md` §۵.۸ made a probe's answer a **read**: the façade queues a CWMP task,
 * a later verification pass GPVs the board's diagnostics object, and whatever the board wrote there
 * is what gets published. Before that the console computed `rtt_ms`, a hop list and an Mbps pair out
 * of `hash(device + target)` and put them in a field named like the server's contract — a number the
 * repository cannot get from any document, wearing the shape of evidence. So this file holds an
 * object instead of a formula.
 *
 * What is declared here, and on whose authority:
 *  - the **prefixes** are `acs_service/diagnostics.py::CANDIDATES`, i.e. TR-098's named
 *    single-instance objects with `Device.Diagnostics.` as the TR-181 container — survey 03 §۲
 *    [S1] is the document that names them for this fleet;
 *  - the **leaf names** are the list that same survey row prints
 *    (`DiagnosticsState, Interface, Host, NumberOfRepetitions, Timeout, DataBlockSize, DSCP,
 *    SuccessCount, FailureCount, AverageResponseTime, MinimumResponseTime, MaximumResponseTime`
 *    for ping; `DiagnosticsState, Interface, DownloadURL, DSCP, EthernetPriority,
 *    TotalBytesReceived` for throughput). `TraceRouteDiagnostics` is only named as
 *    `TraceRouteDiagnostics.*` in [S1], so the traceroute board answers with the two leaves that
 *    list does carry over (`DiagnosticsState`, `Host`) and this fixture invents no hop table;
 *  - the **values** are demo data for a console screenshot. They are a fixture's normal invented
 *    numbers — the point is that they arrive the way a board's arrive: as leaves of a named object,
 *    read after the task, attributed to the host this probe asked for.
 *
 * `Host`/`DownloadURL` is filled with the request's own target because that is what a board that ran
 * this probe reports; the server's attribution rule (`ATTRIBUTION_LEAVES`) then decides whether the
 * object belongs to this job. `staleHost` exists so the *other* answer is demoable too: an object
 * left behind by an earlier probe names a different host, and publishing it would pass someone
 * else's leftovers off as this probe's result.
 */
import type { DiagnosticsType } from '../api/types';

/**
 * One object's worth of leaves, exactly as a GPV of `prefix` returns them: **keyed by full path**,
 * because `diagnostics.py::read` keys `observed` by `row["path"]` and the published event carries
 * those keys. A leaf name alone would be a second, friendlier shape for the same fact.
 */
export interface ProbeObject {
  /** The prefix the GPV was issued on — CANDIDATES' entry, the container on TR-181. */
  readonly readPrefix: string;
  /** The object the answered leaves hang off, published as `read_from`. */
  readonly prefix: string;
  readonly leaves: Readonly<Record<string, unknown>>;
}

/** `prefix` + the leaf names the board answers with, as full paths. */
function under(prefix: string, named: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(named)) out[`${prefix}${name}`] = value;
  return out;
}

/** How the fixture board answers this probe. Mirrors `diagnostics.py::settle`'s six cases. */
export type ProbeFate = 'answered' | 'pending' | 'no-object' | 'unattributed' | 'faulted';

/** The TR-098 object names, per `acs_service/diagnostics.py::CANDIDATES`. */
const TR098_PREFIX: Readonly<Record<DiagnosticsType, string>> = {
  ping: 'InternetGatewayDevice.IPPingDiagnostics.',
  traceroute: 'InternetGatewayDevice.TraceRouteDiagnostics.',
  speedtest: 'InternetGatewayDevice.DownloadDiagnostics.',
};

/**
 * TR-181 keeps one container that this repository's document names (`Device.Diagnostics.`, §۵.۸'s
 * table), and the objects live inside it as instances — the façade GPVs the container and publishes
 * whichever object carries this probe's host, so `readPrefix` and `prefix` differ here and are the
 * same string on TR-098.
 */
const TR181_READ_PREFIX = 'Device.Diagnostics.';
const TR181_OBJECT: Readonly<Record<DiagnosticsType, string>> = {
  ping: 'IPPingDiagnostics',
  traceroute: 'TraceRouteDiagnostics',
  speedtest: 'DownloadDiagnostics',
};

/** A board that refused the probe: `9001` is §۵.۲'s «رد عمومی بدون دلیل». */
export const REFUSED_FAULT_CODE = 9001;

/** The host an earlier probe left in the object when the fate is `unattributed`. */
export const STALE_PROBE_HOST = '198.51.100.2';

function pingLeaves(target: string): Record<string, unknown> {
  return {
    DiagnosticsState: 'Complete',
    Interface: 'WAN_IP',
    Host: target,
    NumberOfRepetitions: 4,
    Timeout: 2000,
    DataBlockSize: 64,
    DSCP: 0,
    SuccessCount: 4,
    FailureCount: 0,
    AverageResponseTime: 19,
    MinimumResponseTime: 12,
    MaximumResponseTime: 31,
  };
}

function traceLeaves(target: string): Record<string, unknown> {
  // [S1] names the object but not its leaves, so only the two it does carry over are answered.
  return { DiagnosticsState: 'Complete', Host: target };
}

function downloadLeaves(target: string): Record<string, unknown> {
  return {
    DiagnosticsState: 'Complete',
    Interface: 'WAN_IP',
    DownloadURL: target,
    DSCP: 0,
    EthernetPriority: 0,
    TotalBytesReceived: 104_857_600,
  };
}

/** `diagnostics.py::CANDIDATES`: the order the object is looked for, TR-098 then the container. */
export function candidatesFor(probe: DiagnosticsType): readonly string[] {
  return [TR098_PREFIX[probe], TR181_READ_PREFIX];
}

function leavesFor(probe: DiagnosticsType, target: string): Record<string, unknown> {
  if (probe === 'ping') return pingLeaves(target);
  if (probe === 'traceroute') return traceLeaves(target);
  return downloadLeaves(target);
}

function namesTheHost(probe: DiagnosticsType, host: string): Record<string, unknown> {
  const leaves = { ...leavesFor(probe, host) };
  // The attribution leaf is the one that changed; `DownloadURL` for throughput, `Host` otherwise.
  if (probe === 'speedtest') leaves.DownloadURL = host;
  else leaves.Host = host;
  return leaves;
}

/**
 * The object this fixture board has, or `null` when it has none.
 *
 * `tr181` switches the prefix to the container because that is what a TR-181 board answers under;
 * the other fates change what the board put in the object, not where it lives.
 */
export function probeObjectFor(
  probe: DiagnosticsType,
  target: string,
  options: { tr181?: boolean; fate?: ProbeFate } = {},
): ProbeObject | null {
  const fate = options.fate ?? 'answered';
  if (fate === 'no-object' || fate === 'pending' || fate === 'faulted') return null;
  const readPrefix = options.tr181 ? TR181_READ_PREFIX : TR098_PREFIX[probe];
  const prefix = options.tr181
    ? `${TR181_READ_PREFIX}1.${TR181_OBJECT[probe]}.`
    : TR098_PREFIX[probe];
  const named =
    fate === 'unattributed' ? namesTheHost(probe, STALE_PROBE_HOST) : leavesFor(probe, target);
  // An unattributed container read is published with the prefix that was read, like the server does.
  const keyPrefix = fate === 'unattributed' ? readPrefix : prefix;
  return { readPrefix, prefix, leaves: under(keyPrefix, named) };
}
